import { useCallback, useMemo, useState } from 'react';
import {
  hasModuleAccess,
  MODULE_KEYS,
  type ModuleKey,
  type OrgModuleAccess,
} from '../constants/moduleAccess.ts';
import { useOrgModuleAccess } from './useOrgModuleAccess.ts';

const MODULE_LABELS: Record<ModuleKey, string> = {
  [MODULE_KEYS.CONTROLS]: 'Controls',
  [MODULE_KEYS.SOX_ISSUES]: 'SOX Issues',
  [MODULE_KEYS.TEST_EXECUTIONS]: 'Test Executions',
  [MODULE_KEYS.RISKS]: 'Risks',
  [MODULE_KEYS.PROCESSES]: 'Processes',
  [MODULE_KEYS.MATERIALITY]: 'Materiality',
  [MODULE_KEYS.REPORTS]: 'Reports',
  [MODULE_KEYS.SYSTEMS]: 'Systems',
  [MODULE_KEYS.SOF]: 'SOF',
  [MODULE_KEYS.EVIDENCE]: 'Evidence',
  [MODULE_KEYS.FILES]: 'Files',
};

type UseRequireModuleAccessOptions = {
  hint?: string;
};

export function useRequireModuleAccess(
  moduleKey: ModuleKey,
  options: UseRequireModuleAccessOptions = {}
) {
  const { data: moduleAccess, isSuccess } = useOrgModuleAccess();
  const accessReady = isSuccess && moduleAccess != null;
  const canAccess = useMemo(
    () => (accessReady ? hasModuleAccess(moduleAccess, moduleKey) : false),
    [accessReady, moduleAccess, moduleKey]
  );
  const [showDenied, setShowDenied] = useState(false);
  const label = MODULE_LABELS[moduleKey] || moduleKey;

  const checkAccess = useCallback((): boolean => {
    // Fail closed while module-access is still loading / unknown.
    if (!accessReady) return false;
    if (canAccess) return true;
    setShowDenied(true);
    return false;
  }, [accessReady, canAccess]);

  const dialogProps = useMemo(
    () => ({
      open: showDenied,
      onClose: () => setShowDenied(false),
      title: `No ${label} module access`,
      message:
        options.hint ||
        `This action requires the ${label} module. Contact your organization administrator if you need access.`,
    }),
    [showDenied, label, options.hint]
  );

  return { canAccess, checkAccess, showDenied, setShowDenied, dialogProps };
}

export function useRequireAllModuleAccess(
  moduleKeys: ModuleKey[],
  options: UseRequireModuleAccessOptions = {}
) {
  const { data: moduleAccess, isSuccess } = useOrgModuleAccess();
  const accessReady = isSuccess && moduleAccess != null;
  const canAccess = useMemo(
    () => (accessReady ? moduleKeys.every((key) => hasModuleAccess(moduleAccess, key)) : false),
    [accessReady, moduleAccess, moduleKeys]
  );
  const [showDenied, setShowDenied] = useState(false);
  const labels = moduleKeys.map((key) => MODULE_LABELS[key] || key);

  const checkAccess = useCallback((): boolean => {
    // Fail closed while module-access is still loading / unknown.
    if (!accessReady) return false;
    if (canAccess) return true;
    setShowDenied(true);
    return false;
  }, [accessReady, canAccess]);

  const dialogProps = useMemo(
    () => ({
      open: showDenied,
      onClose: () => setShowDenied(false),
      title:
        labels.length > 1
          ? `No ${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]} module access`
          : `No ${labels[0]} module access`,
      message:
        options.hint ||
        (labels.length > 1
          ? `This action requires the ${labels.join(' and ')} modules. Contact your organization administrator if you need access.`
          : `This action requires the ${labels[0]} module. Contact your organization administrator if you need access.`),
    }),
    [showDenied, labels, options.hint]
  );

  return { canAccess, checkAccess, showDenied, setShowDenied, dialogProps };
}

export function getMissingModuleKeys(
  access: OrgModuleAccess | undefined,
  moduleKeys: ModuleKey[]
): ModuleKey[] {
  return moduleKeys.filter((key) => !hasModuleAccess(access, key));
}

export function formatModuleAccessDeniedCopy(
  moduleKeys: ModuleKey[],
  hint?: string
): { title: string; message: string } {
  const labels = moduleKeys.map((key) => MODULE_LABELS[key] || key);
  return {
    title:
      labels.length > 1
        ? `No ${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]} module access`
        : `No ${labels[0]} module access`,
    message:
      hint ||
      (labels.length > 1
        ? `This action requires the ${labels.join(' and ')} modules. Contact your organization administrator if you need access.`
        : `This action requires the ${labels[0]} module. Contact your organization administrator if you need access.`),
  };
}
